/**
 * Core-vs-additional key split for the public data-input submission.
 *
 * Mirror of website-builder-backend `src/lib/data-input-split.ts`
 * (`splitDataInputValues`), itself a port of the editable subset of
 * `GuestValidation.BulkUpdateFields` in the JVM.
 *
 * WHY THIS EXISTS: the runtime collects a single flat `values` map keyed by
 * each input's `data-field-variable`, which mixes core GuestDoc columns
 * (firstName, email, …) with custom additional-field keys. The client-side
 * gate in `dataInput.tsx` validates against the event's additional-field
 * schema ONLY — so without this split, every core column is wrongly reported
 * as `unknownAdditionalFieldKey` and submission is blocked before the POST,
 * even though the backend would accept it (the backend validates only the
 * `extraValues` subset). Keep {@link CORE_GUEST_COLUMNS} and
 * {@link DROPPED_CORE_KEYS} byte-equal with the backend copy.
 */

/** Top-level GuestDoc columns the public submit path may write. */
export const CORE_GUEST_COLUMNS: ReadonlySet<string> = new Set([
  "firstName",
  "lastName",
  "gender",
  "title",
  "jobTitle",
  "company",
  "email",
  "phone",
  "mobile",
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "language",
  "emailOptIn",
  "state",
]);

/**
 * Recognised GuestDoc columns the SPA may post but the public path never
 * mutates (e.g. the Decline button posts both `state` and `acceptanceState`).
 * Dropped silently — not validated as additional fields.
 *
 * `role` + `questions` are builder form variables that map to no writeable
 * column: `role` is the staff permission (`event_role.role`) — a reserved key
 * that must never be set from a public form — and `questions` is a legacy
 * free-text variable that is not a registered additional field. Dropping both
 * lets such forms submit (their values are discarded). Keep this set
 * byte-equal with backend data-input-split.ts.
 *
 * `sessions` (session-selection P4): the session-picker payload (desired
 * complete set of programItemIds). It lives in the `session_registrations`
 * join collection — NEVER in guest additionalFields — and is consumed as a
 * structural top-level body key by `/data-input/submit`; if it ever lands
 * inside `values` (a widget wiring slip) it is dropped, not validated as a
 * custom field. Twin of the same key in backend data-input-split.ts.
 */
export const DROPPED_CORE_KEYS: ReadonlySet<string> = new Set([
  "acceptanceState",
  "archived",
  "checkedIn",
  "checkedInAt",
  "username",
  "code",
  "imageUrl",
  "role",
  "questions",
  "sessions",
]);

/**
 * Return only the keys that are neither a writeable core column nor a
 * dropped core key — i.e. the custom additional-field values the backend
 * routes to its additional-field validator (`mainSplit.extraValues`).
 * Pass the result to `validateSubmissionClientSide` so core columns aren't
 * mis-flagged as `unknownAdditionalFieldKey`.
 */
export function selectAdditionalFieldValues(
  values: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const extras: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values ?? {})) {
    if (DROPPED_CORE_KEYS.has(key)) continue;
    if (CORE_GUEST_COLUMNS.has(key)) continue;
    extras[key] = value;
  }
  return extras;
}
